import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { showAlert } from 'APP/Tasks/app/app';
import { GroupType } from 'APP/model/group/groupModel.types';
import { ServerErrorCode } from 'APP/restApi';
import { GroupServerErrorCode } from 'APP/restApi/constants/group.errors';
import { Group } from 'APP/store/Groups/Group';

interface IParticipantSelectList {
  selectedParticipantIds: string[];
  isSubmitting: boolean;
  isAddButtonDisabled: boolean;
  buttonText: string;

  setSelectedIds(ids: string[]): void;
  addParticipants(): Promise<void>;
}

const knownError: Record<string, string> = {
  [GroupServerErrorCode.InvitationsTooManyToAdd]: 'common_group_alert_user_many_add',

  [ServerErrorCode.HasNoPermissionsToPerformAction]: 'no_permission_alert',
  [GroupServerErrorCode.GroupDoesNotExist]: 'common_chat_is_not_accessible',
  [GroupServerErrorCode.UserIsBannedInTheGroup]: 'common_group_alert_user_ban',
  [GroupServerErrorCode.UserGroupRelationshipDoesNotExist]:
    'common_group_alert_relationship_not_exit',
  [ServerErrorCode.SpaceDoesNotExist]: 'common_group_alert_space_not_exit',
  [ServerErrorCode.TooManyInvitedMembersInShortTime]: 'common_invitation_daily_limit_alert',
};

export interface IParticipantSelectListPresenterParams {
  group: Group;
  onSuccess?(): void;
}

export const useParticipantSelectList = ({
  group,
  onSuccess,
}: IParticipantSelectListPresenterParams): IParticipantSelectList => {
  const { t } = useTranslation();

  const source = useAsObservableSource({ group });

  const presenter = useLocalStore<IParticipantSelectList>(() => ({
    participantIds: [],
    selectedParticipantIds: [],
    searchValue: '',
    isLoading: true,
    isSubmitting: false,

    get buttonText(): string {
      return source.group.isChannel ? t('common_invite') : t('add_button');
    },

    get isAddButtonDisabled(): boolean {
      return presenter.selectedParticipantIds.length === 0;
    },

    setSelectedIds: (ids: string[]): void => {
      presenter.selectedParticipantIds = ids;
    },

    addParticipants: async (): Promise<void> => {
      presenter.isSubmitting = true;

      const groupRequest =
        group.type === GroupType.SecretGroup
          ? Tasks.e2ee.addMembersToSecretGroup
          : Tasks.group.addParticipants;

      const { error } = await groupRequest(source.group.id, presenter.selectedParticipantIds);

      presenter.isSubmitting = false;

      if (error) {
        showAlert(t(knownError[error] || 'common_somethings_wrong_error'));
        return;
      }

      onSuccess?.();
    },
  }));

  return presenter;
};
